import { z } from "zod";

import type { TextComposerAuthoringState } from "./authoringState";
import type {
  TextComposerExtension,
  ExtensionValidationResult,
  FrameVariantContext,
  TransformContext,
} from "./extensionContract";
import type {
  TextComposerSmsPayload,
  TextComposerRcsPayload,
  TextComposerMmsPayload,
} from "../channel/channelTransforms";
import type { FrameVariantConfigParsed } from "@/frames/schemas";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPrimaryKey(ext: TextComposerExtension): string | undefined {
  return ext.persistence?.ownedKeys[0];
}

// ---------------------------------------------------------------------------
// composeAuthoringSchema
// ---------------------------------------------------------------------------

/**
 * Builds a strict Zod schema for the in-store authoring state from the
 * registered extensions. Each extension contributes a slice keyed by its
 * `persistence.ownedKeys[0]`. Adding a new extension does not require
 * changes to any core file beyond `defaultExtensions.ts`.
 */
export function buildAuthoringDraftSchema(
  extensions: TextComposerExtension[],
): z.ZodType<TextComposerAuthoringState> {
  const shape: Record<string, z.ZodTypeAny> = {
    bodyText: z.string().optional(),
  };

  for (const ext of extensions) {
    if (!ext.stateSchema || !ext.persistence) continue;
    const primaryKey = getPrimaryKey(ext);
    if (!primaryKey) continue;
    if (primaryKey in shape) {
      throw new Error(
        `[TextComposer] Owned-key collision: extension "${ext.id}" claims "${primaryKey}" which is already in the schema`,
      );
    }
    shape[primaryKey] = ext.stateSchema.optional();
  }

  return z
    .object(shape)
    .strict() as unknown as z.ZodType<TextComposerAuthoringState>;
}

// ---------------------------------------------------------------------------
// assertNoOwnedKeyCollisions
// ---------------------------------------------------------------------------

/**
 * Throws if two extensions claim overlapping `ownedKeys`. Run at provider
 * initialisation so misconfigurations surface immediately.
 */
export function assertNoOwnedKeyCollisions(
  extensions: TextComposerExtension[],
): void {
  const claimedBy = new Map<string, string>();
  for (const ext of extensions) {
    if (!ext.persistence) continue;
    for (const key of ext.persistence.ownedKeys) {
      const existing = claimedBy.get(key);
      if (existing && existing !== ext.id) {
        throw new Error(
          `[TextComposer] Owned-key collision on "${key}" between extensions "${existing}" and "${ext.id}"`,
        );
      }
      claimedBy.set(key, ext.id);
    }
  }
}

// ---------------------------------------------------------------------------
// resolveExtensionOrder
// ---------------------------------------------------------------------------

/**
 * Topologically sorts extensions according to their `dependencies.before/after`.
 * Throws on cycles. Extensions without dependencies retain their input order.
 */
export function resolveExtensionOrder(
  extensions: TextComposerExtension[],
): TextComposerExtension[] {
  const byId = new Map<string, TextComposerExtension>();
  const indexById = new Map<string, number>();
  for (let i = 0; i < extensions.length; i++) {
    const ext = extensions[i];
    if (byId.has(ext.id)) {
      throw new Error(
        `[TextComposer] Duplicate extension id "${ext.id}" in registry`,
      );
    }
    byId.set(ext.id, ext);
    indexById.set(ext.id, i);
  }

  const adjacency = new Map<string, Set<string>>();
  const inDegree = new Map<string, number>();
  for (const ext of extensions) {
    adjacency.set(ext.id, new Set());
    inDegree.set(ext.id, 0);
  }

  function addEdge(fromId: string, toId: string) {
    if (!byId.has(fromId) || !byId.has(toId)) return;
    const edges = adjacency.get(fromId)!;
    if (!edges.has(toId)) {
      edges.add(toId);
      inDegree.set(toId, (inDegree.get(toId) ?? 0) + 1);
    }
  }

  for (const ext of extensions) {
    const deps = ext.dependencies;
    if (!deps) continue;
    for (const target of deps.before ?? []) {
      addEdge(ext.id, target);
    }
    for (const source of deps.after ?? []) {
      addEdge(source, ext.id);
    }
  }

  // Kahn's algorithm with stable order: pick the lowest input-index node
  // among those with no remaining dependencies.
  const result: TextComposerExtension[] = [];
  const remaining = new Set(extensions.map((e) => e.id));

  while (remaining.size > 0) {
    let pickedId: string | undefined;
    let pickedIndex = Number.POSITIVE_INFINITY;
    for (const id of remaining) {
      if ((inDegree.get(id) ?? 0) !== 0) continue;
      const idx = indexById.get(id) ?? Number.POSITIVE_INFINITY;
      if (idx < pickedIndex) {
        pickedIndex = idx;
        pickedId = id;
      }
    }

    if (pickedId === undefined) {
      const remainingIds = Array.from(remaining).join(", ");
      throw new Error(
        `[TextComposer] Cyclic dependency among extensions: ${remainingIds}`,
      );
    }

    result.push(byId.get(pickedId)!);
    remaining.delete(pickedId);
    for (const next of adjacency.get(pickedId) ?? []) {
      inDegree.set(next, (inDegree.get(next) ?? 1) - 1);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// composeNormalize — produces the clean in-store authoring state
// ---------------------------------------------------------------------------

/**
 * Returns a function that yields a fresh authoring state containing only
 * non-empty `bodyText` and non-empty extension slices (post-`normalize`).
 */
export function composeNormalize(
  extensions: TextComposerExtension[],
): (state: TextComposerAuthoringState) => TextComposerAuthoringState {
  return (state) => {
    const source = state as TextComposerAuthoringState & {
      [key: string]: unknown;
    };
    const next: TextComposerAuthoringState & { [key: string]: unknown } = {};

    if (
      typeof source.bodyText === "string" &&
      source.bodyText.trim().length > 0
    ) {
      next.bodyText = source.bodyText;
    }

    for (const ext of extensions) {
      const primaryKey = getPrimaryKey(ext);
      if (!primaryKey) continue;
      const slice = source[primaryKey];
      if (slice === undefined) continue;
      const normalized = ext.normalize
        ? (ext.normalize as (s: unknown) => unknown)(slice)
        : slice;
      if (ext.isEmpty && (ext.isEmpty as (s: unknown) => boolean)(normalized)) {
        continue;
      }
      next[primaryKey] = normalized;
    }

    return next;
  };
}

// ---------------------------------------------------------------------------
// composeSerialize / composeDeserialize — round-trip with the wire format
// ---------------------------------------------------------------------------

/**
 * Builds a serialiser that produces the wire-format `authoringState` blob.
 * Each extension contributes its top-level keys via `persistence.serialize`.
 * Falls back to copying the slice at the primary key when no persistence is
 * declared (kept for compatibility but rarely used).
 */
export function composeSerialize(
  extensions: TextComposerExtension[],
): (state: TextComposerAuthoringState) => Record<string, unknown> {
  return (state) => {
    const source = state as TextComposerAuthoringState & {
      [key: string]: unknown;
    };
    const wire: Record<string, unknown> = {};
    if (typeof source.bodyText === "string") {
      wire.bodyText = source.bodyText;
    }
    for (const ext of extensions) {
      if (!ext.persistence) continue;
      const primaryKey = getPrimaryKey(ext);
      if (!primaryKey) continue;
      const slice = source[primaryKey];
      if (slice === undefined) continue;
      const contributed = (
        ext.persistence.serialize as (s: unknown) => Record<string, unknown>
      )(slice);
      Object.assign(wire, contributed);
    }
    return wire;
  };
}

/**
 * Builds a deserialiser that reconstructs the in-store authoring state from
 * the wire `authoringState` blob. Extensions whose `persistence.deserialize`
 * returns `undefined` contribute nothing.
 */
export function composeDeserialize(
  extensions: TextComposerExtension[],
): (raw: Record<string, unknown>) => TextComposerAuthoringState {
  return (raw) => {
    const next: TextComposerAuthoringState & { [key: string]: unknown } = {};
    if (typeof raw.bodyText === "string") {
      next.bodyText = raw.bodyText;
    }
    for (const ext of extensions) {
      if (!ext.persistence) continue;
      const primaryKey = getPrimaryKey(ext);
      if (!primaryKey) continue;
      const slice = (
        ext.persistence.deserialize as (r: Record<string, unknown>) => unknown
      )(raw);
      if (slice === undefined) continue;
      next[primaryKey] = slice;
    }
    return next;
  };
}

// ---------------------------------------------------------------------------
// collectContentContributions
// ---------------------------------------------------------------------------

interface ContentContributionResult {
  text: boolean;
  media: boolean;
}

/**
 * Collects all extension content-contribution declarations and merges them
 * into a single `{ text, media }` result. Order-independent.
 */
export function collectContentContributions(
  extensions: TextComposerExtension[],
  state: TextComposerAuthoringState,
): ContentContributionResult {
  let text = false;
  let media = false;

  for (const ext of extensions) {
    if (ext.contentContribution) {
      const contribution = ext.contentContribution(state);
      if (contribution) {
        if (contribution.text) text = true;
        if (contribution.media) media = true;
      }
    }
  }

  return { text, media };
}

// ---------------------------------------------------------------------------
// composeTransforms
// ---------------------------------------------------------------------------

interface ComposedTransforms {
  transformSms: (
    state: TextComposerAuthoringState,
    payload: TextComposerSmsPayload,
    context: TransformContext,
  ) => TextComposerSmsPayload;

  transformRcs: (
    state: TextComposerAuthoringState,
    payload: TextComposerRcsPayload,
    context: TransformContext,
  ) => TextComposerRcsPayload;

  transformMms: (
    state: TextComposerAuthoringState,
    payload: TextComposerMmsPayload,
    context: TransformContext,
  ) => TextComposerMmsPayload;
}

/**
 * Returns transform runners that pipe each channel's payload through every
 * extension's transform hook in registration order.
 */
export function composeTransforms(
  extensions: TextComposerExtension[],
): ComposedTransforms {
  return {
    transformSms(state, payload, context) {
      let current = payload;
      for (const ext of extensions) {
        if (ext.transformSms) {
          current = ext.transformSms(state, current, context);
        }
      }
      return current;
    },

    transformRcs(state, payload, context) {
      let current = payload;
      for (const ext of extensions) {
        if (ext.transformRcs) {
          current = ext.transformRcs(state, current, context);
        }
      }
      return current;
    },

    transformMms(state, payload, context) {
      let current = payload;
      for (const ext of extensions) {
        if (ext.transformMms) {
          current = ext.transformMms(state, current, context);
        }
      }
      return current;
    },
  };
}

// ---------------------------------------------------------------------------
// Frame-variant config contributions
// ---------------------------------------------------------------------------

/**
 * Returns the union of all `frameVariantConfigKeys` declared by the registry.
 * The save-payload builder uses this to strip stale values before applying
 * fresh contributions.
 */
export function collectFrameVariantConfigKeys(
  extensions: TextComposerExtension[],
): Set<string> {
  const keys = new Set<string>();
  for (const ext of extensions) {
    for (const key of ext.frameVariantConfigKeys ?? []) {
      keys.add(key);
    }
  }
  return keys;
}

/**
 * Folds every extension's `contributeFrameVariantConfig` into a single
 * config object. Extensions that contribute `null` (or omit the method)
 * contribute nothing.
 */
export function composeFrameVariantConfig(
  extensions: TextComposerExtension[],
): (
  state: TextComposerAuthoringState,
  context: FrameVariantContext,
) => Partial<FrameVariantConfigParsed> {
  return (state, context) => {
    const result: Partial<FrameVariantConfigParsed> = {};
    for (const ext of extensions) {
      const contribution = ext.contributeFrameVariantConfig?.(state, context);
      if (contribution !== null && contribution !== undefined) {
        Object.assign(result, contribution);
      }
    }
    return result;
  };
}

// ---------------------------------------------------------------------------
// composeValidation
// ---------------------------------------------------------------------------

export interface ComposedValidationResult {
  valid: boolean;
  extensions: Record<string, ExtensionValidationResult>;
}

/**
 * Runs every extension's `validate` hook and aggregates results.
 * The top-level `valid` is true only when ALL extensions report valid.
 */
export function composeValidation(
  extensions: TextComposerExtension[],
): (state: TextComposerAuthoringState) => ComposedValidationResult {
  return (state) => {
    const results: Record<string, ExtensionValidationResult> = {};
    let allValid = true;

    for (const ext of extensions) {
      if (ext.validate) {
        const result = ext.validate(state);
        results[ext.id] = result;
        if (!result.valid) {
          allValid = false;
        }
      }
    }

    return { valid: allValid, extensions: results };
  };
}
